import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Pencil, Trash2, Plus, LogOut, X, Save, ImagePlus, Star, MessageSquare } from "lucide-react";
import { api, formatPrice, CATEGORIES, imgUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ImageUploader from "../components/ImageUploader";

const EMPTY = {
  name: "",
  description: "",
  price: 0,
  category: "bois",
  image_url: "",
  images: [],
  in_stock: true,
};

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [tab, setTab] = useState("products");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  const loadReviews = () => api.get("/reviews/admin/all").then((r) => setReviews(r.data));

  useEffect(() => {
    if (user && user.role === "admin") {
      load();
      loadReviews();
    }
  }, [user]);

  if (loading) return <div className="p-10 text-center text-stone-500">Chargement…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  const handleSave = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = {
        ...editing,
        price: parseFloat(editing.price) || 0,
        in_stock: !!editing.in_stock,
        images: (editing.images || []).filter(Boolean),
      };
      if (!payload.image_url) {
        setError("Une image principale est requise.");
        setBusy(false);
        return;
      }
      if (editing.id) {
        await api.put(`/products/${editing.id}`, payload);
      } else {
        await api.post(`/products`, payload);
      }
      setEditing(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    await api.delete(`/products/${id}`);
    await load();
  };

  const handleDeleteReview = async (id) => {
    if (!window.confirm("Supprimer cet avis ?")) return;
    await api.delete(`/reviews/${id}`);
    await loadReviews();
  };

  const addGalleryImage = () => {
    setEditing({ ...editing, images: [...(editing.images || []), ""] });
  };
  const updateGalleryImage = (i, url) => {
    const next = [...(editing.images || [])];
    next[i] = url;
    setEditing({ ...editing, images: next });
  };
  const removeGalleryImage = (i) => {
    const next = [...(editing.images || [])];
    next.splice(i, 1);
    setEditing({ ...editing, images: next });
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-serif text-2xl text-stone-900">Bidoudou50.fr — Admin</h1>
            <p className="text-xs text-stone-500">Connecté : {user.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-view-shop"
              onClick={() => navigate("/")}
              className="px-3 py-2 text-sm border border-stone-300 hover:bg-stone-100 rounded-sm"
            >
              Voir la boutique
            </button>
            <button
              data-testid="admin-logout"
              onClick={() => { logout(); navigate("/"); }}
              className="px-3 py-2 text-sm bg-stone-900 hover:bg-stone-800 text-white rounded-sm inline-flex items-center gap-1.5"
            >
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-center gap-2 mb-8 border-b border-stone-200">
          <button
            data-testid="tab-products"
            onClick={() => setTab("products")}
            className={`px-4 py-2.5 text-sm uppercase tracking-[0.15em] border-b-2 -mb-px transition-colors ${
              tab === "products"
                ? "border-orange-800 text-stone-900 font-semibold"
                : "border-transparent text-stone-500 hover:text-stone-900"
            }`}
          >
            Produits ({products.length})
          </button>
          <button
            data-testid="tab-reviews"
            onClick={() => setTab("reviews")}
            className={`px-4 py-2.5 text-sm uppercase tracking-[0.15em] border-b-2 -mb-px transition-colors ${
              tab === "reviews"
                ? "border-orange-800 text-stone-900 font-semibold"
                : "border-transparent text-stone-500 hover:text-stone-900"
            }`}
          >
            Avis ({reviews.length})
          </button>
        </div>

        {tab === "products" && (
        <>
        <div className="flex items-center justify-between mb-6">
          <div>
            <span className="uppercase tracking-[0.15em] text-xs font-semibold text-stone-500">Catalogue</span>
            <h2 className="font-serif text-3xl text-stone-900">Produits ({products.length})</h2>
          </div>
          <button
            data-testid="add-product-button"
            onClick={() => setEditing({ ...EMPTY })}
            className="px-4 py-2.5 bg-orange-800 hover:bg-orange-900 text-white rounded-sm inline-flex items-center gap-2"
          >
            <Plus size={16} /> Nouveau produit
          </button>
        </div>

        <div data-testid="admin-products-table" className="bg-white border border-stone-200 rounded-sm overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-stone-100 text-left text-xs uppercase tracking-[0.15em] text-stone-500">
              <tr>
                <th className="p-3">Image</th>
                <th className="p-3">Nom</th>
                <th className="p-3">Catégorie</th>
                <th className="p-3">Prix</th>
                <th className="p-3">Stock</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 && (
                <tr><td colSpan={6} className="text-center p-10 text-stone-400">Aucun produit. Cliquez sur "Nouveau produit" pour commencer.</td></tr>
              )}
              {products.map((p) => (
                <tr key={p.id} data-testid={`admin-row-${p.id}`} className="border-t border-stone-100">
                  <td className="p-3">
                    <img src={imgUrl(p.image_url)} alt={p.name} className="w-14 h-14 object-cover rounded-sm bg-stone-100" />
                  </td>
                  <td className="p-3 font-medium text-stone-900">{p.name}</td>
                  <td className="p-3 capitalize text-stone-600">{p.category}</td>
                  <td className="p-3 font-black text-orange-700">{formatPrice(p.price)}</td>
                  <td className="p-3">
                    {p.in_stock ? (
                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-sm">En stock</span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs bg-stone-200 text-stone-700 rounded-sm">Rupture</span>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        data-testid={`edit-product-${p.id}`}
                        onClick={() => setEditing({ ...p, images: p.images || [] })}
                        className="p-2 hover:bg-stone-100 rounded-sm"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        data-testid={`delete-product-${p.id}`}
                        onClick={() => handleDelete(p.id)}
                        className="p-2 hover:bg-red-50 text-red-700 rounded-sm"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
        )}

        {tab === "reviews" && (
          <div>
            <div className="mb-6">
              <span className="uppercase tracking-[0.15em] text-xs font-semibold text-stone-500">Modération</span>
              <h2 className="font-serif text-3xl text-stone-900">Avis clients ({reviews.length})</h2>
              <p className="text-sm text-stone-500 mt-1">Les avis sont publiés automatiquement. Vous pouvez supprimer ceux qui ne respectent pas vos règles.</p>
            </div>
            <div data-testid="admin-reviews-list" className="space-y-3">
              {reviews.length === 0 ? (
                <div className="bg-white border border-stone-200 rounded-sm p-10 text-center text-stone-400">
                  Aucun avis pour le moment.
                </div>
              ) : (
                reviews.map((r) => (
                  <div key={r.id} data-testid={`admin-review-${r.id}`} className="bg-white border border-stone-200 rounded-sm p-5 flex gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-stone-900">{r.author}</span>
                        <div className="inline-flex items-center gap-0.5">
                          {[1,2,3,4,5].map((s) => (
                            <Star key={s} size={14} className={s <= r.rating ? "fill-orange-500 text-orange-500" : "text-stone-300"} />
                          ))}
                        </div>
                        <span className="text-xs text-stone-400">
                          {new Date(r.created_at).toLocaleDateString("fr-FR")}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-stone-700">{r.comment}</p>
                    </div>
                    <button
                      data-testid={`delete-review-${r.id}`}
                      onClick={() => handleDeleteReview(r.id)}
                      className="p-2 hover:bg-red-50 text-red-700 rounded-sm self-start"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </main>

      {editing && (
        <div data-testid="product-form-dialog" className="fixed inset-0 z-50 bg-stone-900/60 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <form
            onSubmit={handleSave}
            onClick={(e) => e.stopPropagation()}
            className="bg-stone-50 max-w-2xl w-full rounded-sm border border-stone-200 max-h-[92vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-5 border-b border-stone-200 sticky top-0 bg-stone-50 z-10">
              <h2 className="font-serif text-2xl text-stone-900">
                {editing.id ? "Modifier le produit" : "Nouveau produit"}
              </h2>
              <button type="button" onClick={() => setEditing(null)} className="p-2 hover:bg-stone-200 rounded-sm">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500">Nom *</label>
                <input
                  data-testid="form-name"
                  required
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="mt-1 w-full px-4 py-2.5 bg-white border border-stone-300 rounded-sm focus:ring-2 focus:ring-orange-800 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500">Description</label>
                <textarea
                  data-testid="form-description"
                  rows={3}
                  value={editing.description || ""}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  className="mt-1 w-full px-4 py-2.5 bg-white border border-stone-300 rounded-sm focus:ring-2 focus:ring-orange-800 focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500">Prix (€) *</label>
                  <input
                    data-testid="form-price"
                    required
                    type="number"
                    step="0.01"
                    min="0"
                    value={editing.price}
                    onChange={(e) => setEditing({ ...editing, price: e.target.value })}
                    className="mt-1 w-full px-4 py-2.5 bg-white border border-stone-300 rounded-sm focus:ring-2 focus:ring-orange-800 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500">Catégorie *</label>
                  <select
                    data-testid="form-category"
                    value={editing.category}
                    onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                    className="mt-1 w-full px-4 py-2.5 bg-white border border-stone-300 rounded-sm focus:ring-2 focus:ring-orange-800 focus:outline-none"
                  >
                    {CATEGORIES.filter((c) => c.key !== "all").map((c) => (
                      <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500">Image principale *</label>
                <div className="mt-2">
                  <ImageUploader
                    testId="form-main-image"
                    value={editing.image_url}
                    onChange={(url) => setEditing({ ...editing, image_url: url })}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500">Galerie (optionnel)</label>
                  <button
                    type="button"
                    data-testid="add-gallery-image"
                    onClick={addGalleryImage}
                    className="text-xs inline-flex items-center gap-1 text-orange-800 hover:text-orange-900"
                  >
                    <ImagePlus size={14} /> Ajouter une photo
                  </button>
                </div>
                <div className="mt-2 space-y-3">
                  {(editing.images || []).map((url, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <div className="flex-1">
                        <ImageUploader
                          testId={`form-gallery-${i}`}
                          value={url}
                          onChange={(u) => updateGalleryImage(i, u)}
                        />
                      </div>
                      <button
                        type="button"
                        data-testid={`remove-gallery-${i}`}
                        onClick={() => removeGalleryImage(i)}
                        className="mt-1 p-2 hover:bg-red-50 text-red-700 rounded-sm"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                  {(!editing.images || editing.images.length === 0) && (
                    <p className="text-xs text-stone-500">Aucune photo additionnelle. Cliquez sur "Ajouter une photo" pour enrichir la galerie produit.</p>
                  )}
                </div>
              </div>

              <label className="inline-flex items-center gap-2 text-sm text-stone-700">
                <input
                  data-testid="form-in-stock"
                  type="checkbox"
                  checked={!!editing.in_stock}
                  onChange={(e) => setEditing({ ...editing, in_stock: e.target.checked })}
                  className="w-4 h-4 accent-orange-800"
                />
                En stock
              </label>

              {error && (
                <div data-testid="form-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">{String(error)}</div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 p-5 border-t border-stone-200 bg-white sticky bottom-0">
              <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 border border-stone-300 rounded-sm hover:bg-stone-100">
                Annuler
              </button>
              <button
                data-testid="form-submit"
                type="submit"
                disabled={busy}
                className="px-5 py-2 bg-orange-800 hover:bg-orange-900 disabled:bg-stone-400 text-white rounded-sm inline-flex items-center gap-2"
              >
                <Save size={16} /> {busy ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
